import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Keyboard, 
  MousePointer, 
  Pencil, 
  Hand, 
  Smartphone, 
  Tablet, 
  Monitor, 
  Clock, 
  Image, 
  MessageSquare,
  Code,
  Copy
} from 'lucide-react';

function HelpPage() {
  const [activeSection, setActiveSection] = useState('keyboard-shortcuts');

  useEffect(() => {
    // Function to handle scroll events and update active section
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      const scrollPosition = window.scrollY + 100; // Offset to trigger the highlight earlier

      // Find the current section in view
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(section.id);
        }
      });
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    // Call once on mount to set initial active section
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Help & Documentation</title>
        <meta name="description" content="Find help and documentation for UIForge. Learn about keyboard shortcuts, canvas tools, AI prompting, and other features." />
      </Helmet>

      <div className="flex h-full bg-white dark:bg-dark-bg-primary">
        {/* Fixed Table of Contents sidebar */}
        <div className="w-64 h-full overflow-y-auto p-4 border-r border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-bg-secondary shrink-0 sticky top-0">
          <h2 className="text-lg font-medium mb-4 dark:text-dark-text-primary">Table of Contents</h2>
          <nav className="space-y-2">
            <a 
              href="#keyboard-shortcuts" 
              className={`block py-1 hover:underline ${activeSection === 'keyboard-shortcuts' 
                ? 'text-blue-700 dark:text-dark-accent font-medium bg-blue-50 dark:bg-blue-900/20 px-2 -mx-2 rounded' 
                : 'text-blue-600 dark:text-blue-400'}`}
            >
              Keyboard Shortcuts
            </a>
            <a 
              href="#canvas-tools" 
              className={`block py-1 hover:underline ${activeSection === 'canvas-tools' 
                ? 'text-blue-700 dark:text-dark-accent font-medium bg-blue-50 dark:bg-blue-900/20 px-2 -mx-2 rounded' 
                : 'text-blue-600 dark:text-blue-400'}`}
            >
              Canvas Tools & Drawing
            </a>
            <a 
              href="#device-selection" 
              className={`block py-1 hover:underline ${activeSection === 'device-selection' 
                ? 'text-blue-700 dark:text-dark-accent font-medium bg-blue-50 dark:bg-blue-900/20 px-2 -mx-2 rounded' 
                : 'text-blue-600 dark:text-blue-400'}`}
            >
              Device Selection
            </a>
            <a 
              href="#version-history" 
              className={`block py-1 hover:underline ${activeSection === 'version-history' 
                ? 'text-blue-700 dark:text-dark-accent font-medium bg-blue-50 dark:bg-blue-900/20 px-2 -mx-2 rounded' 
                : 'text-blue-600 dark:text-blue-400'}`}
            >
              Version History
            </a>
            <a 
              href="#ai-prompting" 
              className={`block py-1 hover:underline ${activeSection === 'ai-prompting' 
                ? 'text-blue-700 dark:text-dark-accent font-medium bg-blue-50 dark:bg-blue-900/20 px-2 -mx-2 rounded' 
                : 'text-blue-600 dark:text-blue-400'}`}
            >
              AI Prompts & Best Practices
            </a>
            <a 
              href="#image-upload" 
              className={`block py-1 hover:underline ${activeSection === 'image-upload' 
                ? 'text-blue-700 dark:text-dark-accent font-medium bg-blue-50 dark:bg-blue-900/20 px-2 -mx-2 rounded' 
                : 'text-blue-600 dark:text-blue-400'}`}
            >
              Image Upload
            </a>
            <a 
              href="#code-view" 
              className={`block py-1 hover:underline ${activeSection === 'code-view' 
                ? 'text-blue-700 dark:text-dark-accent font-medium bg-blue-50 dark:bg-blue-900/20 px-2 -mx-2 rounded' 
                : 'text-blue-600 dark:text-blue-400'}`}
            >
              Code View & Export
            </a>
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 p-6 overflow-y-auto dark:text-dark-text-primary">
          <h1 className="text-2xl font-bold mb-6">UIForge Documentation</h1>
          
          <div className="prose max-w-none dark:prose-invert">
            <p className="text-gray-500 dark:text-dark-text-secondary mb-8">
              Learn how to use all the features available in UIForge to design and prototype your UI faster.
            </p>
            
            {/* Keyboard Shortcuts */}
            <section id="keyboard-shortcuts" className="mb-10 scroll-mt-4">
              <div className="flex items-center mb-4">
                <Keyboard className="mr-2 text-gray-500 dark:text-gray-400" size={24} />
                <h2 className="text-xl font-semibold">Keyboard Shortcuts</h2>
              </div>
              
              <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-dark-border">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-border">
                  <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Shortcut</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-bg-secondary divide-y divide-gray-200 dark:divide-dark-border">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">Ctrl/⌘ + Z</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Undo last action</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">Ctrl/⌘ + Shift + Z</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Redo last action</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">S</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Select tool</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">P</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Pan tool</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">D</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Draw tool</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">Delete / Backspace</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Delete selected objects</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">Esc</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Cancel current action</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">Ctrl/⌘ + C</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Copy selected objects</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">Ctrl/⌘ + V</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Paste objects</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-900 dark:text-dark-text-primary">+/-</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-text-secondary">Zoom in/out</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
            
            {/* Canvas Tools */}
            <section id="canvas-tools" className="mb-10 scroll-mt-4">
              <div className="flex items-center mb-4">
                <Pencil className="mr-2 text-gray-500 dark:text-gray-400" size={24} />
                <h2 className="text-xl font-semibold">Canvas Tools & Drawing</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center">
                    <MousePointer size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-medium">Select Tool</h3>
                  </div>
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-2">
                      Use to select existing elements or drawings. Draw a box around elements to select them.
                    </p>
                    <div className="text-xs bg-gray-100 dark:bg-dark-bg-primary p-2 rounded">
                      <strong>Tip:</strong> After selecting, you can ask the AI to modify the selected elements.
                    </div>
                  </div>
                </div>
                
                <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center">
                    <Hand size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-medium">Pan Tool</h3>
                  </div>
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-2">
                      Click and drag to move around the canvas, especially useful for larger designs.
                    </p>
                    <div className="text-xs bg-gray-100 dark:bg-dark-bg-primary p-2 rounded">
                      <strong>Tip:</strong> You can also use the zoom buttons to zoom in/out.
                    </div>
                  </div>
                </div>
                
                <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center">
                    <Pencil size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-medium">Draw Tool</h3>
                  </div>
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-2">
                      Create rough sketches the AI can transform into actual UI elements.
                    </p>
                    <div className="text-xs bg-gray-100 dark:bg-dark-bg-primary p-2 rounded">
                      <strong>Tip:</strong> Draw boxes, circles, or rough UI element shapes.
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
            {/* Device Selection */}
            <section id="device-selection" className="mb-10 scroll-mt-4">
              <div className="flex items-center mb-4">
                <Smartphone className="mr-2 text-gray-500 dark:text-gray-400" size={24} />
                <h2 className="text-xl font-semibold">Device Selection</h2>
              </div>
              
              <p className="mb-4 dark:text-dark-text-secondary">
                UIForge lets you design for different device types, ensuring your UI works across devices:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="border border-gray-200 dark:border-dark-border rounded-lg p-4 flex flex-col items-center dark:bg-dark-bg-secondary">
                  <Monitor size={32} className="text-blue-600 dark:text-blue-400 mb-2" />
                  <h3 className="font-medium">Web</h3>
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary text-center mt-1">Full-width layout optimized for desktop browsers</p>
                </div>
                
                <div className="border border-gray-200 dark:border-dark-border rounded-lg p-4 flex flex-col items-center dark:bg-dark-bg-secondary">
                  <Tablet size={32} className="text-blue-600 dark:text-blue-400 mb-2" />
                  <h3 className="font-medium">Tablet</h3>
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary text-center mt-1">768×1024 layout optimized for tablet devices</p>
                </div>
                
                <div className="border border-gray-200 dark:border-dark-border rounded-lg p-4 flex flex-col items-center dark:bg-dark-bg-secondary">
                  <Smartphone size={32} className="text-blue-600 dark:text-blue-400 mb-2" />
                  <h3 className="font-medium">Mobile</h3>
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary text-center mt-1">393×852 layout optimized for mobile devices</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 rounded-r-md mb-6">
                <h4 className="font-medium text-yellow-800 dark:text-yellow-500 mb-1">Responsive Design</h4>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  When switching devices, the AI will automatically adjust your design to fit the new screen size while preserving your content and styling choices.
                </p>
              </div>
            </section>
            
            {/* Version History */}
            <section id="version-history" className="mb-10 scroll-mt-4">
              <div className="flex items-center mb-4">
                <Clock className="mr-2 text-gray-500 dark:text-gray-400" size={24} />
                <h2 className="text-xl font-semibold">Version History</h2>
              </div>
              
              <p className="mb-4 dark:text-dark-text-secondary">
                UIForge automatically saves versions of your design as you iterate. You can:
              </p>
              
              <ul className="list-disc pl-6 mb-6 space-y-2">
                <li className="text-gray-600 dark:text-dark-text-secondary">Access previous versions through the version history dropdown</li>
                <li className="text-gray-600 dark:text-dark-text-secondary">Compare current and previous designs</li>
                <li className="text-gray-600 dark:text-dark-text-secondary">Restore previous versions if needed</li>
              </ul>
              
              <p className="text-sm bg-gray-100 dark:bg-dark-bg-secondary p-3 rounded-md border border-gray-200 dark:border-dark-border">
                <strong>Note:</strong> Version history is automatically maintained during your current session, with up to 10 recent versions stored. For permanent storage, use the export functionality.
              </p>
            </section>
            
            {/* AI Prompting */}
            <section id="ai-prompting" className="mb-10 scroll-mt-4">
              <div className="flex items-center mb-4">
                <MessageSquare className="mr-2 text-gray-500 dark:text-gray-400" size={24} />
                <h2 className="text-xl font-semibold">AI Prompts & Best Practices</h2>
              </div>
              
              <p className="mb-4 dark:text-dark-text-secondary">
                The quality of your results depends on how you communicate with the AI. Here are some tips for effective prompting:
              </p>
              
              <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden mb-6">
                <div className="bg-gray-50 dark:bg-dark-bg-secondary p-3">
                  <h4 className="font-medium">Effective Prompt Examples</h4>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-dark-border">
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <h5 className="font-medium text-green-600 dark:text-green-400 mb-1">Initial Design Creation</h5>
                    <p className="text-sm text-gray-700 dark:text-dark-text-primary mb-2">"Create a landing page for a fitness app with a hero section, features list, and pricing table"</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-secondary">This provides clear structure and content requirements.</p>
                  </div>
                  
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <h5 className="font-medium text-green-600 dark:text-green-400 mb-1">Element Modification</h5>
                    <p className="text-sm text-gray-700 dark:text-dark-text-primary mb-2">"Make this button larger, round the corners, and change the color to blue"</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-secondary">Specific changes with visual attributes clearly stated.</p>
                  </div>
                  
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <h5 className="font-medium text-green-600 dark:text-green-400 mb-1">Drawing Conversion</h5>
                    <p className="text-sm text-gray-700 dark:text-dark-text-primary mb-2">"Convert this drawing to a contact form with name, email and message fields"</p>
                    <p className="text-xs text-gray-500 dark:text-dark-text-secondary">Tells the AI both what to create and what content to include.</p>
                  </div>
                </div>
              </div>
              
              <h4 className="font-medium mb-3">Prompting Tips</h4>
              <ul className="list-disc pl-5 space-y-2 mb-4 text-sm text-gray-600 dark:text-dark-text-secondary">
                <li><strong>Be specific about styling</strong> - Mention colors, sizes, and visual properties</li>
                <li><strong>Provide content context</strong> - Tell the AI what kind of content should appear in elements</li>
                <li><strong>Reference design patterns</strong> - Mention standard patterns like "card layout" or "hero section"</li>
                <li><strong>Use clear formatting instructions</strong> - Specify layout, spacing, and alignment</li>
                <li><strong>Break complex requests into steps</strong> - Make one change at a time for best results</li>
              </ul>
            </section>
            
            {/* Image Upload */}
            <section id="image-upload" className="mb-10 scroll-mt-4">
              <div className="flex items-center mb-4">
                <Image className="mr-2 text-gray-500 dark:text-gray-400" size={24} />
                <h2 className="text-xl font-semibold">Image Upload</h2>
              </div>
              
              <p className="mb-4 dark:text-dark-text-secondary">
                You can upload images to include in your designs or as reference for the AI:
              </p>
              
              <div className="bg-gray-50 dark:bg-dark-bg-secondary p-4 rounded-lg mb-4">
                <h4 className="font-medium mb-2">How to Upload Images</h4>
                <ol className="list-decimal pl-5 space-y-1 text-sm text-gray-600 dark:text-dark-text-secondary">
                  <li>Click the attachment button in the chat input</li>
                  <li>Select one or more image files (.jpg, .png, etc.)</li>
                  <li>Add a prompt describing how to use the images</li>
                  <li>Submit your message</li>
                </ol>
              </div>
              
              <div className="border-l-4 border-blue-500 dark:border-blue-400 pl-4 bg-blue-50 dark:bg-blue-900/20 p-3">
                <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-1">Image Upload Use Cases</h4>
                <ul className="list-disc pl-5 text-sm text-blue-700 dark:text-blue-300">
                  <li><strong>Design inspiration</strong> - Upload a reference design for the AI to take cues from</li>
                  <li><strong>Logo/brand assets</strong> - Include your actual logo or brand elements</li>
                  <li><strong>Content images</strong> - Add photos or illustrations to be included in your design</li>
                  <li><strong>UI sketches</strong> - Upload hand-drawn wireframes for the AI to digitize</li>
                </ul>
              </div>
            </section>
            
            {/* Code View & Export */}
            <section id="code-view" className="mb-10 scroll-mt-4">
              <div className="flex items-center mb-4">
                <Code className="mr-2 text-gray-500 dark:text-gray-400" size={24} />
                <h2 className="text-xl font-semibold">Code View & Export</h2>
              </div>
              
              <p className="mb-4 dark:text-dark-text-secondary">
                UIForge generates real HTML that you can view, inspect, and export:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center">
                    <Code size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-medium">Code Viewer</h3>
                  </div>
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-2">
                      Toggle between design and code view to see the HTML behind your design.
                    </p>
                    <div className="text-xs bg-gray-100 dark:bg-dark-bg-primary p-2 rounded">
                      <strong>Features:</strong> Syntax highlighting, line numbers, collapsible sections, and auto-formatting.
                    </div>
                  </div>
                </div>
                
                <div className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 flex items-center">
                    <Copy size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                    <h3 className="font-medium">Export Options</h3>
                  </div>
                  <div className="p-4 dark:bg-dark-bg-secondary">
                    <p className="text-sm text-gray-600 dark:text-dark-text-secondary mb-2">
                      Use the export functionality to get your design's code in different formats.
                    </p>
                    <div className="text-xs bg-gray-100 dark:bg-dark-bg-primary p-2 rounded">
                      <strong>Available formats:</strong> HTML/CSS, React components, or as an image.
                    </div>
                  </div>
                </div>
              </div>
              
              <p className="p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-md text-sm border border-green-200 dark:border-green-800">
                <strong>Pro tip:</strong> The exported code includes comments to help you understand the structure and modify it as needed.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}

export default HelpPage; 